/**
 * useLogbookEntries Hook
 * 
 * Custom hook for managing logbook entries data and fetching.
 */

import * as React from "react";
import { logbookService, type LogbookEntry, type CreateLogbookEntryPayload } from "@/services/logbookService";
import { toast } from "sonner";

interface UseLogbookEntriesProps {
  userId?: string;
  filters?: {
    callsign?: string;
    band?: string;
    mode?: string;
    country?: string;
    dxcc?: number;
    cqZone?: number;
    ituZone?: number;
    qslStatus?: string;
    dateFrom?: string;
    dateTo?: string;
    contestName?: string;
    gridSquare?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  };
}

export function useLogbookEntries({ userId, filters }: UseLogbookEntriesProps = {}) {
  const [entries, setEntries] = React.useState<LogbookEntry[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [total, setTotal] = React.useState(0);

  const fetchEntries = React.useCallback(async () => {
    if (!userId) {
      setEntries([]);
      setTotal(0);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await logbookService.getEntries(filters);
      setEntries(result.data);
      setTotal(result.total);
    } catch (err) {
      console.error("Error fetching logbook entries:", err);
      const errorMessage = err instanceof Error ? err.message : "Failed to load logbook entries";
      setError(errorMessage);
      setEntries([]);
      setTotal(0);
      toast.error("Failed to load logbook entries", {
        description: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  }, [userId, filters]);

  React.useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  return {
    entries,
    loading,
    error,
    total,
    fetchEntries,
  };
}

export function useCreateLogbookEntry() {
  const [creating, setCreating] = React.useState(false);

  const createEntry = React.useCallback(async (payload: CreateLogbookEntryPayload) => {
    setCreating(true);
    try {
      await logbookService.createEntry(payload);
      toast.success("Logbook entry created successfully");
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to create logbook entry";
      toast.error("Failed to create logbook entry", {
        description: errorMessage,
      });
      throw err;
    } finally {
      setCreating(false);
    }
  }, []);

  return {
    createEntry,
    creating,
  };
}

export function useUpdateLogbookEntry() {
  const [updating, setUpdating] = React.useState(false);

  const updateEntry = React.useCallback(async (entryId: string, payload: Partial<CreateLogbookEntryPayload>) => {
    setUpdating(true);
    try {
      await logbookService.updateEntry(entryId, payload);
      toast.success("Logbook entry updated successfully");
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to update logbook entry";
      toast.error("Failed to update logbook entry", {
        description: errorMessage,
      });
      throw err;
    } finally {
      setUpdating(false);
    }
  }, []);

  return {
    updateEntry,
    updating,
  };
}

export function useDeleteLogbookEntry() {
  const [deleting, setDeleting] = React.useState(false);

  const deleteEntry = React.useCallback(async (entryId: string) => {
    setDeleting(true);
    try {
      await logbookService.deleteEntry(entryId);
      toast.success("Logbook entry deleted successfully");
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to delete logbook entry";
      toast.error("Failed to delete logbook entry", {
        description: errorMessage,
      });
      throw err;
    } finally {
      setDeleting(false);
    }
  }, []);

  return {
    deleteEntry,
    deleting,
  };
}

export function useLogbookEntry(entryId: string | undefined) {
  const [entry, setEntry] = React.useState<LogbookEntry | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!entryId) {
      setEntry(null);
      return;
    }

    setLoading(true);
    setError(null);

    logbookService
      .getEntry(entryId)
      .then((data) => {
        setEntry(data);
      })
      .catch((err) => {
        console.error("Error fetching logbook entry:", err);
        const errorMessage = err instanceof Error ? err.message : "Failed to load logbook entry";
        setError(errorMessage);
        toast.error("Failed to load logbook entry", {
          description: errorMessage,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [entryId]);

  return {
    entry,
    loading,
    error,
  };
}

